import React from 'react';
import {
  Show,
  TextField,
  EmailField,
  DateField,
  FunctionField,
  useRecordContext,
  Loading,
} from 'react-admin';
import { Link } from 'react-router-dom';
import { Card, CardContent, Typography, Alert, Box, Chip, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DATE_OPTIONS, DATE_TIME_OPTIONS } from '../../app/app.component';
import { leadsFields } from '../leads.model';
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import AddressField from './addressField.component';
import StatusField from './statusField.component';
import SmsOptInStatusField from './smsOptInStatusField.component';
import EmailOptInStatusField from './emailOptInStatusField.component';
import { SectionedShowLayout, LabeledField } from '../../components/layouts';

const ErrorDetails = () => {
  const record = useRecordContext();

  if (
    !record ||
    !record[leadsFields.errorFlag] ||
    !record[leadsFields.errorDetails]
  ) {
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error">
            Error Details
          </Typography>
          <Alert severity="error">{record[leadsFields.errorDetails]}</Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

const LeadTitle = () => {
  const record = useRecordContext();
  if (!record) return null;

  const name = record[leadsFields.name] || '';

  return (
    <span>
      Lead: {name || record[leadsFields.email] || `#${record[leadsFields.id]}`}
    </span>
  );
};

const LeadShowContent = () => {
  const record = useRecordContext();

  // Wait for record to load before rendering
  if (!record) return <Loading />;

  const sections = [
    {
      title: "Basic Information",
      fields: [
        <LabeledField key="name" label="Name">
          <DefaultPlaceholderField source={leadsFields.name} wrapField={<TextField source={leadsFields.name} label={leadsFields.name} />} />
        </LabeledField>,
        <LabeledField key="email" label="Email">
          <DefaultPlaceholderField source={leadsFields.email} wrapField={<EmailField source={leadsFields.email} label={leadsFields.email} />} />
        </LabeledField>,
        <LabeledField key="phoneNumber" label="Phone Number">
          <DefaultPlaceholderField source={leadsFields.phoneNumber} wrapField={<TextField source={leadsFields.phoneNumber} label={leadsFields.phoneNumber} />} />
        </LabeledField>,
        <LabeledField key="birthDate" label="Date of Birth">
          <DefaultPlaceholderField source={leadsFields.birthDate} wrapField={<DateField options={DATE_OPTIONS} source={leadsFields.birthDate} label={leadsFields.birthDate} />} />
        </LabeledField>,
        <LabeledField key="language" label="Language">
          <DefaultPlaceholderField source={leadsFields.language} wrapField={<TextField source={leadsFields.language} label={leadsFields.language} />} />
        </LabeledField>,
      ],
    },
    {
      title: "Address",
      fields: [
        <LabeledField key="fullAddress" label="Full Address">
          <AddressField />
        </LabeledField>,
        <LabeledField key="address" label="Street Address">
          <DefaultPlaceholderField source={leadsFields.address} wrapField={<TextField source={leadsFields.address} label={leadsFields.address} />} />
        </LabeledField>,
        <LabeledField key="state" label="State">
          <DefaultPlaceholderField source={leadsFields.state} wrapField={<TextField source={leadsFields.state} label={leadsFields.state} />} />
        </LabeledField>,
        <LabeledField key="zipCode" label="ZIP Code">
          <DefaultPlaceholderField source={leadsFields.zipCode} wrapField={<TextField source={leadsFields.zipCode} label={leadsFields.zipCode} />} />
        </LabeledField>,
      ],
    },
    {
      title: "Lead Information",
      fields: [
        <LabeledField key="status" label="Status">
          <StatusField />
        </LabeledField>,
        <LabeledField key="isArchived" label="Archived">
          <FunctionField
            render={(record) => {
              const isArchived =
                record?.[leadsFields.isArchived] ??
                record?.is_archived ??
                record?.isArchived ??
                false;

              return (
                <Chip
                  label={isArchived ? 'Yes' : 'No'}
                  color={isArchived ? 'warning' : 'default'}
                  size="small"
                />
              );
            }}
          />
        </LabeledField>,
        <LabeledField key="archivedReason" label="Archive Reason">
          <FunctionField
            render={(record) => {
              const reason =
                record?.[leadsFields.archivedReason] ??
                record?.archived_reason ??
                record?.archive_reason ??
                record?.archivedReason ??
                record?.archiveReason;

              return reason ? (
                <span>{String(reason)}</span>
              ) : (
                <span style={{ color: '#666', fontStyle: 'italic' }}>n/a</span>
              );
            }}
          />
        </LabeledField>,
        <LabeledField key="sourceOrg" label="Source Organization">
          <DefaultPlaceholderField source={leadsFields.sourceOrg} wrapField={<TextField source={leadsFields.sourceOrg} label={leadsFields.sourceOrg} />} />
        </LabeledField>,
        <LabeledField key="batchId" label="Batch ID">
          <DefaultPlaceholderField source={leadsFields.batchId} wrapField={<TextField source={leadsFields.batchId} label={leadsFields.batchId} />} />
        </LabeledField>,
        <LabeledField key="externalId" label="External ID">
          <DefaultPlaceholderField source={leadsFields.externalId} wrapField={<TextField source={leadsFields.externalId} label={leadsFields.externalId} />} />
        </LabeledField>,
        <LabeledField key="smsOptIn" label="SMS Opt-in Status">
          <SmsOptInStatusField />
        </LabeledField>,
        <LabeledField key="emailOptIn" label="Email Opt-in Status">
          <EmailOptInStatusField />
        </LabeledField>,
        <LabeledField key="smsSubscriptionGroupId" label="SMS Subscription Group ID">
          <FunctionField
            render={(record) => {
              const smsGroupId = record[leadsFields.smsSubscriptionGroupId];
              return smsGroupId ? <span>{smsGroupId}</span> : <span style={{ color: '#666', fontStyle: 'italic' }}>n/a</span>;
            }}
          />
        </LabeledField>,
        <LabeledField key="emailSubscriptionGroupId" label="Email Subscription Group ID">
          <DefaultPlaceholderField source={leadsFields.emailSubscriptionGroupId} wrapField={<TextField source={leadsFields.emailSubscriptionGroupId} label={leadsFields.emailSubscriptionGroupId} />} />
        </LabeledField>,
      ],
    },
    {
      title: "Lifecycle Timestamps",
      fields: [
        <LabeledField key="createdAt" label="Created At">
          <DefaultPlaceholderField source={leadsFields.createdAt} wrapField={<DateField options={DATE_TIME_OPTIONS} source={leadsFields.createdAt} label={leadsFields.createdAt} />} />
        </LabeledField>,
        <LabeledField key="updatedAt" label="Last Updated">
          <DefaultPlaceholderField source={leadsFields.updatedAt} wrapField={<DateField options={DATE_TIME_OPTIONS} source={leadsFields.updatedAt} label={leadsFields.updatedAt} />} />
        </LabeledField>,
        <LabeledField key="signedUpAt" label="Signed Up as Patient At">
          <DefaultPlaceholderField source={leadsFields.signedUpAt} wrapField={<DateField options={DATE_TIME_OPTIONS} source={leadsFields.signedUpAt} label={leadsFields.signedUpAt} />} />
        </LabeledField>,
        <LabeledField key="enrolledAt" label="Enrolled as Patient At">
          <DefaultPlaceholderField source={leadsFields.enrolledAt} wrapField={<DateField options={DATE_TIME_OPTIONS} source={leadsFields.enrolledAt} label={leadsFields.enrolledAt} />} />
        </LabeledField>,
        <LabeledField key="archivedAt" label="Last Archived as Lead At">
          <DefaultPlaceholderField source={leadsFields.archivedAt} wrapField={<DateField options={DATE_TIME_OPTIONS} source={leadsFields.archivedAt} label={leadsFields.archivedAt} />} />
        </LabeledField>,
        <LabeledField key="reactivatedAt" label="Last Reactivated as Lead At">
          <DefaultPlaceholderField source={leadsFields.reactivatedAt} wrapField={<DateField options={DATE_TIME_OPTIONS} source={leadsFields.reactivatedAt} label={leadsFields.reactivatedAt} />} />
        </LabeledField>,
        <Tooltip key="reactivationCount" title="Number of times this lead was re-uploaded or reactivated after being archived." placement="top" arrow>
          <Box component="span" sx={{ display: 'inline-block', width: '100%' }}>
            <LabeledField label="Reactivation Count as Lead">
              <DefaultPlaceholderField source={leadsFields.reactivationCount} wrapField={<TextField source={leadsFields.reactivationCount} label={leadsFields.reactivationCount} />} />
            </LabeledField>
          </Box>
        </Tooltip>,
      ],
    },
    {
      title: "User & Business Data",
      fields: [
        <LabeledField key="userId" label="Converted to Patient">
          <FunctionField
            render={(record) => {
              if (record?.userId) {
                // Enrolled leads are always patients, so link directly to patient detail page
                // Style as Chip (like status field) but clickable to open in new tab
                return (
                  <Chip
                    label={`Patient #${record.userId}`}
                    color="success"
                    size="small"
                    icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => window.open(`/patients/${record.userId}/show`, '_blank', 'noopener,noreferrer')}
                    sx={{
                      cursor: 'pointer',
                      padding: '4px 8px',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  />
                );
              }
              return <Chip label="Not enrolled" color="default" size="small" />;
            }}
          />
        </LabeledField>,
        <LabeledField key="insurancePlan" label="Insurance Plan">
          <DefaultPlaceholderField source={leadsFields.insurancePlan} wrapField={<TextField source={leadsFields.insurancePlan} />} />
        </LabeledField>,
        <LabeledField key="employer" label="Employer">
          <DefaultPlaceholderField source={leadsFields.employer} wrapField={<TextField source={leadsFields.employer} />} />
        </LabeledField>,
      ],
    },
  ];

  return (
    <>
      <SectionedShowLayout sections={sections} />
      <ErrorDetails />
    </>
  );
};

export const LeadShow = () => (
  <Show title={<LeadTitle />}>
    <LeadShowContent />
  </Show>
);

export default LeadShow;
